import _ from 'lodash';
import type { z } from 'zod';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { type QuestionType } from '../legacyExamQuestions/enums';
import { LegacyExamAttributes } from './constants';
import { DBLegacyExamSchema, LegacyExamSchema } from './schemas';

type DBLegacyExam = z.infer<typeof DBLegacyExamSchema>;
type LegacyExam = z.infer<typeof LegacyExamSchema>;

export const LegacyExamCodecConfig: TransformerConfig<
  typeof LegacyExamSchema,
  typeof DBLegacyExamSchema
> = {
  name: 'LegacyExam',
  clientSchema: LegacyExamSchema,
  dbSchema: DBLegacyExamSchema,
  transformers: [
    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: (active as LegacyExam['active']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: (active as DBLegacyExam['active']) === 'true',
        }),
      },
    }),
    // topics: Record<topicId, Question[]> ↔ topics: Record<topicId, Record<questionId, Question>>
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: ['topics'] as const,
        encode: ({ topics }) => ({
          topics: Object.fromEntries(
            Object.entries(topics as LegacyExam['topics']).map(([topicId, questions]) => [
              topicId,
              Object.fromEntries(
                questions.map(q => [q.key, { key: q.key, label: q.label, type: q.type }])
              ),
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['topics'] as const,
        decode: ({ topics }) => ({
          topics: Object.entries(topics as DBLegacyExam['topics']).reduce(
            (acc, [topicId, questions]) => {
              acc[topicId] = Object.values(questions);
              return acc;
            },
            {} as Record<string, Array<{ key: string; label: string; type: QuestionType }>>
          ),
        }),
      },
    }),

    // analytics: compute average on decode, strip average on encode
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: ['analytics'] as const,
        encode: ({ analytics }) => {
          const { average: _, ...dbAnalytics } = analytics as LegacyExam['analytics'];
          return { analytics: dbAnalytics };
        },
      },
      decoder: {
        sourceFields: ['analytics'] as const,
        decode: ({ analytics }) => {
          const dbAnalytics = analytics as DBLegacyExam['analytics'];
          return {
            analytics: {
              ...dbAnalytics,
              average:
                dbAnalytics.uniqueAttempts > 0
                  ? dbAnalytics.aggregateMark / dbAnalytics.uniqueAttempts
                  : 0,
            },
          };
        },
      },
    }),

    // Handle search string updates
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: LegacyExamAttributes.Search,
        dependencies: LegacyExamAttributes.Search,
        encode: exam => {
          let searchStr = '';
          LegacyExamAttributes.Search.forEach(attr => {
            const val = exam[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(exam, LegacyExamAttributes.Search) };
        },
      },
    }),
  ],
};

export const encodeLegacyExamToDb = createEncodeToDbFn(LegacyExamCodecConfig);
export const decodeLegacyExamFromDb = createDecodeFromDbFn(LegacyExamCodecConfig);
